import { AuthContext } from "context/appContext";
import { ProfilClient } from "data/types";
import { useAppDispatch, useAppSelector } from "hooks/hooks";
import React, { useContext, useEffect, useState, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  addLocation,
  getLocationAllDataById,
  initStates,
  nextStep,
  previousStep,
  saveDraftLocation,
  setLocationData,
  updateLocation,
} from "redux/features/location/locationSlice";
import LocationAccessibility from "../components/LocationAccessibility";
import LocationAddress from "../components/LocationAddress";
import LocationAvailibility from "../components/LocationAvailibility";
import LocationDescription from "../components/LocationDescription";
import LocationEquipment from "../components/LocationEquipment";
import LocationEvent from "../components/LocationEvent";
import LocationImages from "../components/LocationImages";
import LocationPlayground from "../components/LocationPlayground";
import LocationProfile from "../components/LocationProfile";
import toast from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";
import {
  HomeIcon,
  MapPinIcon,
  SparklesIcon,
  CurrencyDollarIcon,
  CameraIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import { CheckCircleIcon as CheckCircleSolidIcon } from "@heroicons/react/24/solid";

const LocationCommonLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { id } = useParams();
  const isLactionUpadate = location.pathname.includes("update-location");

  const authContext = useContext(AuthContext);
  const profile = authContext.user?.profil as ProfilClient;

  const locationState = useAppSelector((state) => state.location);

  // Configuration des 6 nouvelles étapes
  const TOTAL_STEPS = 6;
  const [currentModernStep, setCurrentModernStep] = useState(1);
  const progress = (currentModernStep / TOTAL_STEPS) * 100;
  const lastStep = currentModernStep === TOTAL_STEPS;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isNextStep, setIsNextStep] = useState<boolean>(false);
  const [isSubmited, setIsSubmited] = useState<boolean>(false);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const submissionProcessedRef = useRef<boolean>(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const [event, setEvent] = useState<Event>(new Event(""));

  // Configuration moderne des étapes
  const modernSteps = [
    {
      id: 1,
      title: "Profil de l'espace",
      subtitle: "Informations de base",
      icon: HomeIcon,
      color: "from-blue-500 to-cyan-400",
      originalSteps: [1], // LocationProfile seulement
    },
    {
      id: 2,
      title: "Localisation",
      subtitle: "Adresse de votre espace",
      icon: MapPinIcon,
      color: "from-cyan-400 to-teal-400",
      originalSteps: [2], // LocationAddress seulement
    },
    {
      id: 3,
      title: "Événements & Équipements",
      subtitle: "Types d'événements et installations",
      icon: SparklesIcon,
      color: "from-teal-400 to-green-400",
      originalSteps: [3, 4], // LocationEvent + LocationPlayground
    },
    {
      id: 4,
      title: "Tarification",
      subtitle: "Prix et disponibilités",
      icon: CurrencyDollarIcon,
      color: "from-green-400 to-yellow-400",
      originalSteps: [6], // LocationAvailibility
    },
    {
      id: 5,
      title: "Description & Accessibilité",
      subtitle: "Présentation et règles d'accès",
      icon: CheckCircleIcon,
      color: "from-yellow-400 to-orange-400",
      originalSteps: [7], // LocationDescription + LocationAccessibility
    },
    {
      id: 6,
      title: "Galerie Photos",
      subtitle: "Images de votre espace",
      icon: CameraIcon,
      color: "from-orange-400 to-red-400",
      originalSteps: [8], // LocationImages
    },
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
    load();
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [locationState.currentStep]);

  useEffect(() => {
    switch (locationState.loading) {
      case "pending": {
        setIsLoading(true);
        break;
      }
      case "succeeded": {
        if (isLactionUpadate) {
          if (isNextStep) {
            navigate(`/client/locations/`);
          } else {
            setIsLoading(false);
          }
        } else {
          if (locationState.locationData?.id !== null) {
            navigate(`/client/location/completed/${locationState.locationData?.id}`);
          } else if (profile.validation === "approuvé") {
            navigate(`/client/locations/`);
          } else {
            navigate(`/client/demande`);
          }
        }
        break;
      }
      case "failed": {
        setIsLoading(false);
        setIsSubmited(false);
        break;
      }
    }
  }, [locationState.loading]);

  useEffect(() => {
    // Seulement traiter les données si l'utilisateur a cliqué sur "Continuer" ou "Soumettre"
    if ((isNextStep || isSubmited) && !submissionProcessedRef.current) {
      console.log("🎯 useEffect handleData triggered - isNextStep:", isNextStep, "isSubmited:", isSubmited);
      submissionProcessedRef.current = true; // Marquer comme traité pour éviter les doublons
      handleData();
    }
  }, [locationState.locationData, isNextStep, isSubmited]);

  useEffect(() => {
    if (locationState.locationError) {
      console.error("🔴 Location Error:", locationState.locationError);
      console.error("🔴 Current step:", currentModernStep);
      console.error("🔴 Location data:", locationState.locationData);
      // Ne pas afficher le toast d'erreur au chargement initial de la page
      // Seulement lors d'actions utilisateur (isNextStep ou isSubmited)
      if (isNextStep || isSubmited) {
        toast.error("Quelque chose ne fonctionne pas, veuillez réessayer!");
      }
    }
  }, [locationState.locationError, isNextStep, isSubmited, currentModernStep]);

  useEffect(() => {
    locationState.locationErrorValidation?.forEach((element) => {
      toast.error(`Il y a une erreur du : [${element.path}] \n ${element.msg}`);
    });
  }, [locationState.locationErrorValidation]);

  useEffect(() => {
    if (locationState.locationNotExist) {
      navigate("/404");
    }
  }, [locationState.locationNotExist]);

  const load = async () => {
    await dispatch(initStates());

    if (isLactionUpadate) {
      if (id) {
        dispatch(getLocationAllDataById(id));
      } else {
        navigate("/404");
      }
    }
  };

  const handleNextClick = () => {
    // Protection contre les doubles soumissions
    if (isLoading) {
      console.log("🛡️ Double soumission bloquée - déjà en cours de traitement");
      return;
    }

    // Réinitialiser les erreurs et le flag de soumission
    setValidationErrors([]);
    submissionProcessedRef.current = false; // Reset pour permettre une nouvelle soumission

    setIsNextStep(true);

    // Marquer l'étape actuelle comme complétée
    setCompletedSteps((prev) => new Set(Array.from(prev).concat([currentModernStep])));

    // Déclencher la validation des formulaires Zod d'abord
    // Logique adaptée pour les 6 étapes modernes
    switch (currentModernStep) {
      case 1: {
        // Étape 1 : Profil & Description (couvre steps 1 et 7)
        if (locationState.currentStep === 1) {
          setEvent(new Event("chekLocationFormOne"));
        } else {
          setEvent(new Event("chekLocationFormSeven"));
        }
        break;
      }
      case 2: {
        // Étape 2 : Localisation & Accès (couvre steps 2 et 5)
        if (locationState.currentStep <= 2) {
          setEvent(new Event("chekLocationFormTwo"));
        } else {
          setEvent(new Event("chekLocationFormFive"));
        }
        break;
      }
      case 3: {
        // Étape 3 : Événements & Équipements (couvre steps 3 et 4)
        // Déclencher seulement les événements (step 3) - les équipements sont optionnels
        setEvent(new Event("chekLocationFormThree"));
        break;
      }
      case 4: {
        // Étape 4 : Tarification (utilise l'ancien step 6 - availibility)
        setEvent(new Event("chekLocationFormSix"));
        break;
      }
      case 5: {
        // Étape 5 : Description + Accessibilité
        // Déclencher d'abord la description, puis l'accessibilité avec un petit délai
        setEvent(new Event("chekLocationFormSeven")); // Description d'abord
        setTimeout(() => {
          setEvent(new Event("chekLocationFormFive")); // Accessibilité après 50ms
        }, 50);
        break;
      }
      case 6: {
        // Étape 6 : Galerie Photos (utilise l'ancien step 8 - images)
        setEvent(new Event("chekLocationFormEight"));
        break;
      }
      default: {
        setEvent(new Event(""));
        break;
      }
    }

    // Validation de sécurité seulement APRÈS que les données soient transmises
    // Ne pas faire de validation automatique - seulement quand l'utilisateur clique sur "Continuer"
    setTimeout(() => {
      // La validation se fait maintenant seulement dans useEffect quand les données changent
      // Pas de validation automatique ici
      setValidationErrors([]); // Nettoyer les erreurs précédentes
    }, 100);
  };

  const handlePreviousClick = () => {
    setIsNextStep(false);
    setCurrentModernStep((prev) => Math.max(prev - 1, 1));
  };

  // Fonction de validation pour chaque étape
  const validateCurrentStep = (step: number, data: any): string[] => {
    const errors: string[] = [];

    switch (step) {
      case 1: // Profil
        if (!data?.title?.trim()) errors.push("Le nom de l'espace est obligatoire");
        if (!data?.categoryLocationId) errors.push("La catégorie est obligatoire");
        if (!data?.surface) errors.push("La surface est obligatoire");
        if (!data?.visitor) errors.push("La capacité d'accueil est obligatoire");
        if (!data?.nbreHour) errors.push("Le nombre d'heures est obligatoire");
        break;
      case 2: // Localisation
        if (!data?.city?.trim()) errors.push("La ville est obligatoire");
        if (!data?.address?.trim()) errors.push("L'adresse est obligatoire");
        if (!data?.codePostale) errors.push("Le code postal est obligatoire");
        break;
      case 3: // Événements & Équipements
        if (!data?.events?.length) errors.push("Au moins un type d'événement est obligatoire");
        // Equipment et playground sont optionnels, pas de validation stricte nécessaire
        break;
      case 4: // Tarification
        // Validation plus stricte pour la tarification
        if (!data?.prices || data.prices.length === 0) {
          errors.push("Au moins un tarif doit être configuré");
        }
        if (!data?.reservation_type || data.reservation_type.trim() === "") {
          errors.push("La politique d'annulation est obligatoire");
        }
        // Vérifier qu'au moins un prix a été réellement défini (pas juste des valeurs par défaut)
        if (data?.prices && data.prices.length > 0) {
          const hasValidPrices = data.prices.some((price: any) => price.price > 0);
          if (!hasValidPrices) {
            errors.push("Vous devez définir au moins un prix valide (supérieur à 0€)");
          }
        }
        break;
      case 5: // Description & Accessibilité
        if (!data?.description?.trim() || data.description.length < 10)
          errors.push("La description est obligatoire (minimum 10 caractères)");
        if (data?.handicap === undefined || data?.handicap === null)
          errors.push("L'accessibilité aux personnes à mobilité réduite est obligatoire");
        if (!data?.parking?.trim()) errors.push("L'accès au stationnement est obligatoire");
        if (!data?.wc?.trim()) errors.push("L'accès aux toilettes est obligatoire");
        if (data?.water === undefined || data?.water === null)
          errors.push("L'accès au point d'eau est obligatoire");
        if (data?.light === undefined || data?.light === null)
          errors.push("L'éclairage extérieur est obligatoire");
        break;
      case 6: // Images
        const totalImages = (data?.images?.length || 0) + (data?.newImages?.length || 0);
        if (totalImages === 0) errors.push("Au moins une image est obligatoire");
        break;
    }

    return errors;
  };

  const handleData = async () => {
    if (locationState.locationData) {
      // Validation des données seulement quand l'utilisateur clique sur "Continuer"
      if (isNextStep && !lastStep) {
        // Pour l'étape 5, ajouter un délai pour laisser le temps aux composants de se synchroniser
        if (currentModernStep === 5) {
          console.log("🔥 ÉTAPE 5 - Délai de validation activé");
          console.log("🔥 Données avant validation:", locationState.locationData);
          setTimeout(() => {
            console.log("🔥 VALIDATION APRÈS 200MS - Données actuelles:", locationState.locationData);
            const validationErrors = validateCurrentStep(currentModernStep, locationState.locationData);
            console.log("🔥 Erreurs de validation:", validationErrors);
            if (validationErrors.length > 0) {
              setValidationErrors(validationErrors);
              setIsNextStep(false);
              toast.error("Veuillez corriger les erreurs avant de continuer");
              return; // Arrêter la progression
            }
            // Si pas d'erreurs, continuer à l'étape suivante
            console.log("✅ Validation réussie - Passage à l'étape 6");
            setValidationErrors([]);
            setCurrentModernStep((prev) => prev + 1);
            setIsNextStep(false);
          }, 200);
          return; // Arrêter l'exécution immédiate pour l'étape 5
        }

        const validationErrors = validateCurrentStep(currentModernStep, locationState.locationData);
        if (validationErrors.length > 0) {
          setValidationErrors(validationErrors);
          setIsNextStep(false);
          toast.error("Veuillez corriger les erreurs avant de continuer");
          return; // Arrêter la progression
        }
        // Si pas d'erreurs, nettoyer et continuer
        setValidationErrors([]);
      }

      if (lastStep && !isSubmited && !isLoading) {
        // Forcer l'ajout des données d'accessibilité si elles manquent
        const finalData = { ...locationState.locationData };

        // Si c'était un brouillon, changer le statut pour soumission
        if (finalData.validation === "brouillon") {
          finalData.validation = "en cours"; // Passer de brouillon à en cours (soumis)
          console.log("Changing status from brouillon to en cours");
        }

        // S'assurer que tous les champs d'accessibilité sont présents
        if (finalData.handicap === undefined || finalData.handicap === null) {
          finalData.handicap = false; // valeur par défaut
        }
        if (!finalData.parking || finalData.parking.trim() === "") {
          finalData.parking = "libre"; // valeur par défaut
        }
        if (!finalData.wc || finalData.wc.trim() === "") {
          finalData.wc = "intérieur"; // valeur par défaut
        }
        if (finalData.water === undefined || finalData.water === null) {
          finalData.water = false; // valeur par défaut
        }
        if (finalData.light === undefined || finalData.light === null) {
          finalData.light = false; // valeur par défaut
        }

        // Nettoyer les champs problématiques
        if (finalData.map === "") {
          (finalData as any).map = null; // Le backend préfère peut-être null à une chaîne vide
        }

        // S'assurer que les prix ont au moins une valeur par défaut si vides
        if (!finalData.prices || finalData.prices.length === 0) {
          console.log("Adding default price structure");
          finalData.prices = []; // Laisser vide mais s'assurer que c'est un tableau
        }

        // Vérifier et nettoyer les champs liés à Stripe
        if (
          (finalData as any).stripeProductId === null ||
          (finalData as any).stripeProductId === undefined ||
          (finalData as any).stripeProductId === ""
        ) {
          delete (finalData as any).stripeProductId; // Supprimer le champ au lieu d'envoyer null
          console.log("Removed null/empty stripeProductId");
        }

        // Nettoyer d'autres champs Stripe potentiellement problématiques
        if (
          (finalData as any).stripePriceId === null ||
          (finalData as any).stripePriceId === undefined ||
          (finalData as any).stripePriceId === ""
        ) {
          delete (finalData as any).stripePriceId;
          console.log("Removed null/empty stripePriceId");
        }

        // S'assurer que les champs requis pour Stripe sont présents
        if (finalData.validation === "en cours") {
          console.log("Preparing data for Stripe integration");
          // Le backend créera automatiquement les produits Stripe nécessaires
        }

        // Convertir availableDays de tableau vers objet si nécessaire
        if (Array.isArray((finalData as any).availableDays)) {
          console.log("Converting availableDays from array to object:", (finalData as any).availableDays);
          const availableDaysArray = (finalData as any).availableDays;
          const availableDaysObject = {
            Lundi: availableDaysArray.includes("Lundi"),
            Mardi: availableDaysArray.includes("Mardi"),
            Mercredi: availableDaysArray.includes("Mercredi"),
            Jeudi: availableDaysArray.includes("Jeudi"),
            Vendredi: availableDaysArray.includes("Vendredi"),
            Samedi: availableDaysArray.includes("Samedi"),
            Dimanche: availableDaysArray.includes("Dimanche"),
          };
          (finalData as any).availableDays = availableDaysObject;
          console.log("Converted availableDays to:", availableDaysObject);
        }

        // S'assurer que availableDays est présent si manquant
        if (!(finalData as any).availableDays) {
          (finalData as any).availableDays = {
            Lundi: true,
            Mardi: true,
            Mercredi: true,
            Jeudi: true,
            Vendredi: true,
            Samedi: true,
            Dimanche: true,
          } as any;
        }

        // Aussi pour compatibilité avec l'ancienne API
        if (!(finalData as any).selectedAvailableDays) {
          (finalData as any).selectedAvailableDays = (finalData as any).availableDays;
        }

        // S'assurer que les autres données essentielles sont présentes avec les bons noms pour l'API
        if (!finalData.equipment) {
          finalData.equipment = [];
        }
        if (!finalData.extraEquipment) {
          finalData.extraEquipment = [];
        }
        if (!(finalData as any).selectedPlayground) {
          (finalData as any).selectedPlayground = finalData.playground || [];
        }
        if (!(finalData as any).selectedAtmospheres) {
          (finalData as any).selectedAtmospheres = finalData.atmosphere || [];
        }
        // Assurer le bon format pour events (le backend s'attend au champ 'events')
        if (!finalData.events || !Array.isArray(finalData.events) || finalData.events.length === 0) {
          // Si pas d'événements sélectionnés, utiliser une valeur par défaut avec le nom de l'événement
          (finalData as any).events = ["Concert"] as any; // Nom par défaut de l'événement
        }
        // selectedEvents pour compatibilité avec l'ancienne API
        if (!(finalData as any).selectedEvents) {
          (finalData as any).selectedEvents = finalData.events;
        }
        if (!(finalData as any).nbreVisitor) {
          (finalData as any).nbreVisitor = finalData.visitor;
        }
        if (!(finalData as any).fullAddress) {
          (finalData as any).fullAddress = finalData.address2 || "";
        }
        if (!finalData.rules) {
          finalData.rules = [];
        }
        if (!finalData.condition) {
          finalData.condition = [];
        }
        if (!finalData.prices) {
          finalData.prices = [];
        }
        if (!finalData.cleaning_costs) {
          finalData.cleaning_costs = 0;
        }
        if (!finalData.guarantee && finalData.guarantee !== 0) {
          finalData.guarantee = 0;
        }
        if (!finalData.reservation_type) {
          finalData.reservation_type = "flexible";
        }

        console.log("🚀 ANALYSE COMPLÈTE DES DONNÉES:");
        console.log("=== ÉTAPE 1 - PROFIL ===");
        console.log("Title:", finalData.title);
        console.log("CategoryLocationId:", finalData.categoryLocationId);
        console.log("Surface:", finalData.surface);
        console.log("Visitor/nbreVisitor:", finalData.visitor, (finalData as any).nbreVisitor);

        console.log("=== ÉTAPE 2 - LOCALISATION ===");
        console.log("Address:", finalData.address);
        console.log("Address2/fullAddress:", finalData.address2, (finalData as any).fullAddress);
        console.log("City:", finalData.city);
        console.log("CodePostale:", finalData.codePostale);
        console.log("Map:", finalData.map);

        console.log("=== ÉTAPE 3 - ÉVÉNEMENTS & ÉQUIPEMENTS ===");
        console.log("Events:", finalData.events, "Length:", finalData.events?.length);
        console.log("Equipment:", finalData.equipment, "Length:", finalData.equipment?.length);
        console.log("ExtraEquipment:", finalData.extraEquipment, "Length:", finalData.extraEquipment?.length);
        console.log("Playground:", finalData.playground, "Length:", finalData.playground?.length);
        console.log("Atmosphere:", finalData.atmosphere, "Length:", finalData.atmosphere?.length);

        console.log("=== ÉTAPE 4 - TARIFICATION ===");
        console.log("Prices:", finalData.prices, "Length:", finalData.prices?.length);
        console.log("Guarantee:", finalData.guarantee);
        console.log("Cleaning_costs:", finalData.cleaning_costs);
        console.log("Reservation_type:", finalData.reservation_type);
        console.log("AvailableDays:", (finalData as any).availableDays);

        console.log("=== ÉTAPE 5 - ACCESSIBILITÉ ===");
        console.log("Handicap:", finalData.handicap);
        console.log("Parking:", finalData.parking);
        console.log("WC:", finalData.wc);
        console.log("Water:", finalData.water);
        console.log("Light:", finalData.light);
        console.log("Rules:", finalData.rules, "Length:", finalData.rules?.length);
        console.log("Condition:", finalData.condition, "Length:", finalData.condition?.length);

        console.log("=== ÉTAPE 6 - IMAGES ===");
        console.log("Images:", finalData.images, "Length:", finalData.images?.length);
        console.log("NewImages:", finalData.newImages, "Length:", finalData.newImages?.length);

        // Nettoyer et corriger les données avant envoi
        const finalDataAny = finalData as any;
        const cleanedData = {
          ...finalData,
          // Corriger fullAddress
          fullAddress:
            finalDataAny.fullAddress ||
            `${finalData.address}, ${finalData.city}, ${finalData.codePostale}, ${finalData.country}`,
          // Supprimer les doublons de visiteurs
          nbreVisitor: finalData.visitor,
          // Nettoyer selectedAvailableDays - s'assurer que c'est un objet valide
          selectedAvailableDays: finalData.availableDays || {},
          // S'assurer que les tableaux vides sont bien des tableaux
          atmosphere: finalData.atmosphere || [],
          equipment: finalData.equipment || [],
          extraEquipment: finalData.extraEquipment || [],
          selectedPlayground: finalDataAny.selectedPlayground || [],
          selectedAtmospheres: finalDataAny.selectedAtmospheres || [],
          rules: finalData.rules || [],
          condition: finalData.condition || [],
          cleaning_costs_events: finalData.cleaning_costs_events || [],
          // IMPORTANT: Inclure les images !
          images: finalData.images || [],
          newImages: finalData.newImages || [],
        };

        console.log("=== DONNÉES NETTOYÉES POUR ENVOI ===", cleanedData);

        setIsLoading(true); // Marquer comme en cours de traitement
        setIsSubmited(true);
        if (isLactionUpadate) {
          console.log("Updating location with data:", cleanedData, "ID:", id);
          const result = await dispatch(updateLocation({ data: cleanedData, id }));
          console.log("Update result:", result);
          console.log("Update payload:", result.payload);

          if (result.payload.status === "VALIDATION") {
            console.error("❌ Validation errors:", result.payload.msg);
            result.payload.msg.forEach((error: any) => {
              console.error(`- Field [${error.path}]: ${error.msg}`);
            });
          } else if (result.payload.status === "ERROR") {
            console.error("❌ Update error:", result.payload.message);
          } else if (result.payload.status === "OK") {
            console.log("✅ Update successful!", result.payload.data);
            console.log("✅ New validation status:", result.payload.data.validation);
          } else {
            console.error("❓ Unknown response status:", result.payload.status);
          }
        } else {
          console.log("Adding new location with data:", cleanedData);
          setIsLoading(true); // Marquer comme en cours de traitement
          setIsSubmited(true); // Marquer comme soumis
          dispatch(addLocation(cleanedData));
        }

        return;
      }

      if (isNextStep && !lastStep) {
        // Avancer à l'étape suivante si les données ont été mises à jour
        setCurrentModernStep((prev) => Math.min(prev + 1, TOTAL_STEPS));
        setIsLoading(false);
      }
    }
  };

  const handleSaveAndExit = async () => {
    try {
      setIsLoading(true);

      // Préparer les données pour le brouillon (sans validation stricte)
      const draftData = {
        ...locationState.locationData,
        validation: "brouillon", // Marquer explicitement comme brouillon
      };

      // Utiliser saveDraftLocation qui ne fait pas de validation stricte
      const result = await dispatch(saveDraftLocation(draftData));

      if (result.payload?.status === "OK") {
        toast.success("Brouillon sauvegardé avec succès !");
        navigate("/client/locations"); // Rediriger vers la liste des espaces
      }
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde du brouillon");
      console.error("Error saving draft:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 relative">
      {/* Overlay de loading moderne pour publication */}
      {isLoading && lastStep && isSubmited && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-2xl border border-gray-200 dark:border-gray-700 max-w-sm mx-4"
          >
            <div className="text-center">
              <div className="relative mb-6">
                <div className="w-16 h-16 mx-auto">
                  <div className="absolute inset-0 rounded-full border-4 border-blue-200 dark:border-blue-800"></div>
                  <div className="absolute inset-0 rounded-full border-4 border-blue-500 border-t-transparent animate-spin"></div>
                  <div className="absolute inset-2 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center">
                    <CheckCircleIcon className="w-6 h-6 text-white animate-pulse" />
                  </div>
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Publication en cours...</h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                Votre espace est en cours de publication. Veuillez patienter.
              </p>
              <div className="mt-4 flex justify-center">
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                  <div
                    className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"
                    style={{ animationDelay: "0.1s" }}
                  ></div>
                  <div
                    className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"
                    style={{ animationDelay: "0.2s" }}
                  ></div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
      <div className="container mx-auto px-4 py-8">
        {/* Header moderne */}
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="text-left mb-12">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            {isLactionUpadate ? "Modifier votre espace" : "Créer votre espace"}
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400 mb-2">
            Bonjour{" "}
            <span className="font-semibold text-blue-600">
              {profile?.name} {profile?.lastName}
            </span>
          </p>
          <p className="text-gray-500 dark:text-gray-400">
            Suivez ces {TOTAL_STEPS} étapes pour configurer votre espace
          </p>
        </motion.div>

        {/* Indicateur d'étapes moderne */}
        <div className="mb-12">
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            {modernSteps.map((step, index) => {
              const isCompleted = completedSteps.has(step.id);
              const isCurrent = currentModernStep === step.id;
              const IconComponent = step.icon;

              return (
                <motion.div
                  key={step.id}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.1 }}
                  className={`
                    relative flex flex-col items-center p-4 rounded-2xl border-2 transition-all duration-300 min-w-[160px]
                    ${
                      isCurrent
                        ? "border-blue-400 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30 shadow-lg transform scale-105"
                        : isCompleted
                          ? "border-green-400 bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/30 dark:to-green-800/30"
                          : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600"
                    }
                  `}
                >
                  {/* Badge de numéro */}
                  <div
                    className={`
                    absolute -top-3 -left-3 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold
                    ${
                      isCurrent
                        ? "bg-blue-500 text-white"
                        : isCompleted
                          ? "bg-green-500 text-white"
                          : "bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-300"
                    }
                  `}
                  >
                    {isCompleted ? <CheckCircleSolidIcon className="w-5 h-5" /> : step.id}
                  </div>

                  {/* Icône principale */}
                  <div
                    className={`
                    w-12 h-12 rounded-xl flex items-center justify-center mb-3 transition-all duration-300
                    ${
                      isCurrent
                        ? `bg-gradient-to-r ${step.color} text-white shadow-lg`
                        : isCompleted
                          ? "bg-green-500 text-white"
                          : "bg-gray-100 dark:bg-gray-700 text-gray-400"
                    }
                  `}
                  >
                    <IconComponent className="w-6 h-6" />
                  </div>

                  {/* Texte */}
                  <div className="text-center">
                    <h3
                      className={`
                      text-sm font-semibold mb-1 transition-colors
                      ${
                        isCurrent
                          ? "text-blue-600 dark:text-blue-400"
                          : isCompleted
                            ? "text-green-600 dark:text-green-400"
                            : "text-gray-600 dark:text-gray-400"
                      }
                    `}
                    >
                      {step.title}
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{step.subtitle}</p>
                  </div>

                  {/* Animation de pulse pour l'étape active */}
                  {isCurrent && (
                    <motion.div
                      className="absolute inset-0 rounded-2xl border-2 border-blue-400"
                      animate={{ scale: [1, 1.05, 1] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    />
                  )}
                </motion.div>
              );
            })}
          </div>

          {/* Barre de progression moderne */}
          <div className="w-full max-w-2xl mx-auto">
            <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
              <span>Progression</span>
              <span>{Math.round(progress)}% complété</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
              <motion.div
                className="bg-gradient-to-r from-blue-500 to-green-500 h-2 rounded-full transition-all duration-700 ease-out"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>

        {/* Affichage des erreurs de validation */}
        {validationErrors.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl"
          >
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-medium text-red-800 dark:text-red-200 mb-2">
                  Veuillez corriger les erreurs suivantes :
                </h3>
                <ul className="list-disc list-inside space-y-1 text-sm text-red-700 dark:text-red-300">
                  {validationErrors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
        )}

        {/* Sauvegarde rapide */}
        {!isLactionUpadate && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex justify-end mb-6">
            <button
              onClick={handleSaveAndExit}
              disabled={false}
              className="flex items-center space-x-2 px-4 py-2 text-sm font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
              <span>Sauvegarder et quitter</span>
            </button>
          </motion.div>
        )}

        {/* Contenu principal avec panneau d'aide */}
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Colonne principale 2/3 */}
          <div className="w-full lg:w-2/3">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentModernStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-8 mb-8"
              >
                {/* Rendu des étapes selon le groupement moderne */}
                {currentModernStep === 1 && (
                  <div className="space-y-8">
                    <div className="text-left mb-8">
                      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                        🏠 Profil de l'espace
                      </h2>
                      <p className="text-gray-600 dark:text-gray-400">
                        Commencez par les informations de base de votre espace
                      </p>
                    </div>

                    <LocationProfile
                      isLoading={isLoading}
                      handleForm={event}
                      data={locationState.locationData}
                      FormData={(data) => dispatch(setLocationData({ ...locationState.locationData, ...data }))}
                    />
                  </div>
                )}

                {currentModernStep === 2 && (
                  <div className="space-y-8">
                    <div className="text-left mb-8">
                      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">📍 Localisation</h2>
                      <p className="text-gray-600 dark:text-gray-400">Où se trouve votre espace</p>
                    </div>

                    <LocationAddress
                      isLoading={isLoading}
                      handleForm={event}
                      data={locationState.locationData}
                      FormData={(data) => dispatch(setLocationData({ ...locationState.locationData, ...data }))}
                    />
                  </div>
                )}

                {currentModernStep === 3 && (
                  <div className="space-y-8">
                    <div className="text-left mb-8">
                      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                        🎉 Événements & Équipements
                      </h2>
                      <p className="text-gray-600 dark:text-gray-400">
                        Quels événements accueillez-vous et quels équipements proposez-vous
                      </p>
                    </div>

                    {/* Événements - Toujours dans l'étape 3 moderne */}
                    <LocationEvent
                      isLoading={isLoading}
                      handleForm={event}
                      data={locationState.locationData}
                      FormData={(data) => dispatch(setLocationData({ ...locationState.locationData, ...data }))}
                    />

                    {/* Équipements - Ajouté à l'étape 3 moderne */}
                    <LocationEquipment
                      isLoading={isLoading}
                      handleForm={event}
                      data={locationState.locationData}
                      FormData={(data) => dispatch(setLocationData({ ...locationState.locationData, ...data }))}
                    />

                    {/* Playground/Activités - Ajouté à l'étape 3 moderne */}
                    <LocationPlayground
                      isLoading={isLoading}
                      handleForm={event}
                      data={locationState.locationData}
                      FormData={(data) => dispatch(setLocationData({ ...locationState.locationData, ...data }))}
                    />
                  </div>
                )}

                {currentModernStep === 4 && (
                  <div className="space-y-8">
                    <div className="text-left mb-8">
                      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">💰 Tarification</h2>
                      <p className="text-gray-600 dark:text-gray-400">Définissez vos tarifs et disponibilités</p>
                    </div>

                    <LocationAvailibility
                      isLoading={isLoading}
                      handleForm={event}
                      data={locationState.locationData}
                      FormData={(data) => dispatch(setLocationData({ ...locationState.locationData, ...data }))}
                    />
                  </div>
                )}

                {currentModernStep === 5 && (
                  <div className="space-y-8">
                    <div className="text-left mb-8">
                      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                        📝 Description et Accessibilité
                      </h2>
                      <p className="text-gray-600 dark:text-gray-400">
                        Complétez votre description et définissez les règles d'accessibilité
                      </p>
                    </div>

                    <LocationDescription
                      isLoading={isLoading}
                      handleForm={event}
                      data={locationState.locationData}
                      FormData={(data) => dispatch(setLocationData({ ...locationState.locationData, ...data }))}
                    />

                    <LocationAccessibility
                      isLoading={isLoading}
                      handleForm={event}
                      data={locationState.locationData}
                      FormData={(data) => dispatch(setLocationData({ ...locationState.locationData, ...data }))}
                    />
                  </div>
                )}

                {currentModernStep === 6 && (
                  <div className="space-y-8">
                    <div className="text-left mb-8">
                      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">📸 Galerie Photos</h2>
                      <p className="text-gray-600 dark:text-gray-400">
                        Ajoutez des photos pour mettre en valeur votre espace
                      </p>
                    </div>

                    <LocationImages
                      isLoading={isLoading}
                      handleForm={event}
                      data={locationState.locationData}
                      FormData={(data) => dispatch(setLocationData({ ...locationState.locationData, ...data }))}
                    />
                  </div>
                )}
              </motion.div>
            </AnimatePresence>

            {/* Navigation moderne */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center justify-between"
            >
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handlePreviousClick}
                disabled={currentModernStep <= 1}
                className="flex items-center space-x-2 px-6 py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ArrowLeftIcon className="w-5 h-5" />
                <span>Précédent</span>
              </motion.button>

              <div className="text-center">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Étape {currentModernStep} sur {TOTAL_STEPS}
                </span>
              </div>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleNextClick}
                disabled={isLoading}
                className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                    <span>Traitement...</span>
                  </>
                ) : (
                  <>
                    <span>{lastStep ? "Publier l'espace" : "Continuer"}</span>
                    {!lastStep ? <ArrowRightIcon className="w-5 h-5" /> : <CheckCircleIcon className="w-5 h-5" />}
                  </>
                )}
              </motion.button>
            </motion.div>
          </div>

          {/* Panneau d'aide moderne 1/3 */}
          <div className="w-full lg:w-1/3">
            <div className="sticky top-6">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-2xl p-6 border border-blue-200 dark:border-blue-700/50 shadow-lg"
              >
                <h3 className="text-xl font-bold text-blue-900 dark:text-blue-100 mb-6 flex items-center">
                  Conseils pour cette étape
                </h3>

                {/* Conseils dynamiques selon l'étape */}
                {currentModernStep === 1 && (
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <HomeIcon className="w-6 h-6 text-blue-600 dark:text-blue-400 mt-1 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold text-blue-800 dark:text-blue-200 mb-1">
                          Informations essentielles
                        </h4>
                        <p className="text-sm text-blue-700 dark:text-blue-300">
                          Renseignez le nom, la catégorie, la superficie et la capacité de votre espace. Ces
                          informations aident les clients à comprendre votre offre.
                        </p>
                      </div>
                    </div>
                    <div className="bg-blue-100 dark:bg-blue-800/30 rounded-lg p-4">
                      <p className="text-xs text-blue-800 dark:text-blue-200">
                        <span className="font-semibold">💡 Astuce :</span> Une description détaillée et attractive
                        augmente vos chances de réservation de 40% !
                      </p>
                    </div>
                  </div>
                )}

                {currentModernStep === 2 && (
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <MapPinIcon className="w-6 h-6 text-blue-600 dark:text-blue-400 mt-1 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold text-blue-800 dark:text-blue-200 mb-1">
                          Localisation précise
                        </h4>
                        <p className="text-sm text-blue-700 dark:text-blue-300">
                          L'adresse complète et les informations d'accès sont essentielles. Précisez les
                          contraintes éventuelles (parking, accessibilité...).
                        </p>
                      </div>
                    </div>
                    <div className="bg-blue-100 dark:bg-blue-800/30 rounded-lg p-4">
                      <p className="text-xs text-blue-800 dark:text-blue-200">
                        <span className="font-semibold">🗺️ Info :</span> L'adresse exacte n'est partagée qu'après
                        la réservation confirmée.
                      </p>
                    </div>
                  </div>
                )}

                {currentModernStep === 3 && (
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <SparklesIcon className="w-6 h-6 text-blue-600 dark:text-blue-400 mt-1 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold text-blue-800 dark:text-blue-200 mb-1">
                          Événements & équipements
                        </h4>
                        <p className="text-sm text-blue-700 dark:text-blue-300">
                          Sélectionnez les types d'événements que vous acceptez et les équipements disponibles.
                          Plus votre offre est complète, plus vous attirez de clients.
                        </p>
                      </div>
                    </div>
                    <div className="bg-blue-100 dark:bg-blue-800/30 rounded-lg p-4">
                      <p className="text-xs text-blue-800 dark:text-blue-200">
                        <span className="font-semibold">⚡ Conseil :</span> Les espaces polyvalents ont 3x plus de
                        réservations !
                      </p>
                    </div>
                  </div>
                )}

                {currentModernStep === 4 && (
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <CurrencyDollarIcon className="w-6 h-6 text-blue-600 dark:text-blue-400 mt-1 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold text-blue-800 dark:text-blue-200 mb-1">
                          Tarification stratégique
                        </h4>
                        <p className="text-sm text-blue-700 dark:text-blue-300">
                          Définissez des prix competitifs et des créneaux de disponibilité clairs. Pensez aux
                          tarifs dégressifs pour les longues durées.
                        </p>
                      </div>
                    </div>
                    <div className="bg-blue-100 dark:bg-blue-800/30 rounded-lg p-4">
                      <p className="text-xs text-blue-800 dark:text-blue-200">
                        <span className="font-semibold">💰 Astuce :</span> Des tarifs flexibles augmentent votre
                        taux d'occupation !
                      </p>
                    </div>
                  </div>
                )}

                {currentModernStep === 5 && (
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <DocumentTextIcon className="w-6 h-6 text-blue-600 dark:text-blue-400 mt-1 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold text-blue-800 dark:text-blue-200 mb-1">
                          Description et accessibilité
                        </h4>
                        <p className="text-sm text-blue-700 dark:text-blue-300">
                          Rédigez une description attractive de votre espace et précisez les informations
                          d'accessibilité. Mentionnez les équipements et services disponibles.
                        </p>
                      </div>
                    </div>
                    <div className="bg-blue-100 dark:bg-blue-800/30 rounded-lg p-4">
                      <p className="text-xs text-blue-800 dark:text-blue-200">
                        <span className="font-semibold">✍️ Conseil :</span> Une description détaillée et
                        l'accessibilité attirent plus de clients !
                      </p>
                    </div>
                  </div>
                )}

                {currentModernStep === 6 && (
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <CameraIcon className="w-6 h-6 text-blue-600 dark:text-blue-400 mt-1 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold text-blue-800 dark:text-blue-200 mb-1">Photos de qualité</h4>
                        <p className="text-sm text-blue-700 dark:text-blue-300">
                          Ajoutez des photos professionnelles et bien éclairées. Montrez différents angles et
                          mettez en valeur les points forts de votre espace.
                        </p>
                      </div>
                    </div>
                    <div className="bg-blue-100 dark:bg-blue-800/30 rounded-lg p-4">
                      <p className="text-xs text-blue-800 dark:text-blue-200">
                        <span className="font-semibold">📸 Important :</span> 85% des clients choisissent selon les
                        photos !
                      </p>
                    </div>
                  </div>
                )}

                {/* Bouton d'aide supplémentaire */}
                <div className="mt-6 pt-4 border-t border-blue-200 dark:border-blue-700">
                  <button className="w-full text-center text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 transition-colors">
                    📞 Besoin d'aide ? Contactez notre équipe
                  </button>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationCommonLayout;
